import React, { useContext, useEffect, useState } from "react";
import axios from "axios";
import { FormikContext } from "formik";

import { UserSearchSelect } from "../../components/inputs";
import { LinkedRequestSlot } from "../../listingFlows/FlowKit.jsx";
import { useQuoteProductPricing } from "../../order/hooks/quoteProductPricing";
import {
  attachListingToRequest,
  createDoneSellRequest,
  fetchRequest,
} from "../../listingFlows/intakeApi";

/**
 * The supplier control for INTERNAL DETAILS & COSTS on the purpose-built flows:
 * the deal links to the **supplier Sell Request** — the record of how we
 * acquired the watch — instead of picking a bare supplier name.
 *
 * Fully live (it used to be display-only, from before the backend link paths
 * existed):
 *
 *   AUTO-SELECT — on mount it reads GET /api/listings/{id}, which resolves the
 *   listing's source Sell Request through BOTH link paths (quotes.listing_id and
 *   requests.linked_listing_id). That one read covers both ways an order item
 *   gets here: an ACTIVE LISTINGS pick (the listing may carry a request from its
 *   own intake) and a DATABASE pick (the listing was created from this order's
 *   quote). A listing with no request behind it simply offers the buttons.
 *
 *   SELECT — persists immediately via POST /api/requests/{id}/attach-listing,
 *   the same write the listing edit screen uses. The backend refuses to steal a
 *   listing another request claims, and that refusal is surfaced here.
 *
 *   CREATE — creates the Sell Request in the background (terminal 'done' status,
 *   linked to this listing at creation) and shows it selected. The client it is
 *   raised for is the listing's own seller (`supplier_id`) — which the backend
 *   seeds from the quote's Product Pricing user when the listing was created
 *   from an order quote, and is whoever was assigned as seller/consignor
 *   otherwise. No seller on the listing ⇒ Create is blocked with the reason,
 *   not a guess: a Sell Request must belong to a real user.
 *
 *   SELLER PICKER — stores without PRODUCT PRICING (useQuoteProductPricing)
 *   have no quote row to seed the seller from, so when the listing has none the
 *   rep picks them here. The pick fills the surrounding costs form's
 *   `supplier` / `supplier_id` (SAVE stores them on the listing, the same
 *   columns the stock flow's SUPPLIER picker writes) and unblocks Create.
 *
 * Once linked (auto, selected or created) the card is read-only — the link
 * lives on the request, and unlinking belongs to the request flow.
 */
const SupplierSellRequestSlot = ({ listing, readOnly = false }) => {
  const listingId = listing?.id ?? null;

  const [loading, setLoading] = useState(!!listingId);
  // {id, name} — the listing's recorded seller, who a created request is for.
  const [seller, setSeller] = useState(null);
  const [productId, setProductId] = useState(listing?.product_id ?? listing?.product?.id ?? null);
  const [selected, setSelected] = useState(null);
  const [linkError, setLinkError] = useState(null);
  const hasProductPricing = useQuoteProductPricing();
  // The costs form this slot sits in (Internal Details & Costs). Read via the
  // context, not useFormikContext(), so the slot still renders outside a form.
  const formik = useContext(FormikContext);
  // True once the rep picked the seller here (vs one already recorded on the
  // listing) — the picker stays up so a wrong pick can be changed.
  const [sellerPicked, setSellerPicked] = useState(false);

  useEffect(() => {
    // Keyed remount + this reset both guard the multi-order case: slot 2's
    // component must never show slot 1's request.
    setSelected(null);
    setSeller(null);
    setSellerPicked(false);
    setLinkError(null);
    setProductId(listing?.product_id ?? listing?.product?.id ?? null);

    if (!listingId) {
      setLoading(false);
      return undefined;
    }

    let alive = true;
    setLoading(true);

    (async () => {
      try {
        const { data } = await axios.get(`/api/listings/${listingId}`);
        if (!alive) return;

        if (data?.supplier_id) {
          setSeller({ id: data.supplier_id, name: data.supplier });
        }
        if (data?.product?.id) {
          setProductId(data.product.id);
        }
        if (data?.source_sell_request) {
          setSelected({
            id: data.source_sell_request.id,
            status: data.source_sell_request.status,
            client: { first_name: data.source_sell_request.client_name },
            product: data.product ?? listing?.product ?? null,
          });
        }
      } catch (e) {
        // Unreadable (deleted, or not this store's listing): the slot stays
        // manual. Create will be blocked below because no seller is known.
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listingId]);

  // SELECT an existing request — persisted before it is shown as linked, so the
  // card never claims a link the backend refused (e.g. the listing is already
  // claimed by another request — a real 422 from attach-listing).
  const selectExisting = async (req) => {
    setLinkError(null);
    try {
      await attachListingToRequest(req.id, listingId);
      setSelected(req);
    } catch (e) {
      setLinkError(
        e?.response?.data?.message ||
          "Could not link that Sell Request to this listing."
      );
    }
  };

  // CREATE instantly: raised for the listing's seller, linked at creation
  // (linked_listing_id), then re-read so the card shows what the API resolved.
  const createNow = async () => {
    const created = await createDoneSellRequest({
      clientId: seller.id,
      productId,
      listingId,
    });
    setSelected(await fetchRequest(created.id).catch(() => created));
  };

  const pickSeller = (option) => {
    if (!option?.id) return;
    setSeller({ id: option.id, name: option.name });
    setSellerPicked(true);
    formik?.setFieldValue("supplier", option.name);
    formik?.setFieldValue("supplier_id", option.id);
  };

  const showSellerPicker =
    !hasProductPricing && !!listingId && !selected && (!seller?.id || sellerPicked);

  const createBlockedNote = !listingId
    ? "The listing must exist before a Sell Request can be linked to it."
    : !seller?.id
      ? hasProductPricing
        ? "This listing has no seller recorded — pick a Product Pricing with a supplier on the quote and press Update Listing, or assign the seller on the listing itself."
        : "This listing has no seller recorded — pick who the watch is bought from above, then Create. SAVE stores the seller on the listing."
      : !productId
        ? "This listing has no product, so a Sell Request cannot be created."
        : null;

  if (loading) {
    return (
      <div className="rc-request-v2__supplier-slot">
        <div className="rc-field__label">Sell Request</div>
        <div className="rc-flow__hint">Checking for a linked Sell Request…</div>
      </div>
    );
  }

  // Costs finalised ⇒ the whole section is approval-gated; the slot follows.
  // The linked card still shows (it is read-only anyway); with nothing linked,
  // the buttons are withheld rather than offered-and-refused.
  if (readOnly) {
    return (
      <div className="rc-request-v2__supplier-slot">
        <LinkedRequestSlot
          selected={selected}
          readOnly
          emptyNote="No Sell Request linked. Costs are finalised — changes here need approval."
        />
      </div>
    );
  }

  return (
    <div className="rc-request-v2__supplier-slot">
      {showSellerPicker && (
        <div className="rc-request-v2__supplier-seller">
          <div className="rc-field__label">Seller</div>
          <UserSearchSelect
            placeholder="Who is the watch bought from?"
            onChange={pickSeller}
          />
        </div>
      )}
      <LinkedRequestSlot
        selected={selected}
        readOnly={!!selected}
        onSelect={selectExisting}
        onCreate={createNow}
        createBlockedNote={createBlockedNote}
      />
      {linkError && (
        <div className="rc-flow__ai rc-flow__ai--unavailable">{linkError}</div>
      )}
    </div>
  );
};

export default SupplierSellRequestSlot;
